import { useSnackbar } from 'notistack';
import useMe from 'data/hooks/useMe';
import { FirebaseDatabaseContext } from 'data/firebase';
import {
  ref,
  onChildAdded,
  onChildChanged,
  DataSnapshot,
} from 'firebase/database';
import { useContext, useEffect, useRef } from 'react';
import { updateMenuStatus } from 'data/cacheHelpers/menu';
import { useQueryClient } from 'react-query';
import { playBeep } from 'utils/sound';

let socket: WebSocket | undefined;

type Subcription = {
  orgId: string;
  unsubscribes: (() => void)[];
};

const shouldIngore = (isoDate: string): boolean => {
  return new Date().getTime() - new Date(isoDate).getTime() > 20 * 1000;
};

// const useSocket = (addModalNotification: AddModalNotificationEvent) => {
const useSocket = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { me } = useMe();
  const database = useContext(FirebaseDatabaseContext);
  const subscription = useRef<Subcription | null>(null);
  const queryCache = useQueryClient();

  useEffect(() => {
    if (me == null && subscription.current != null) {
      subscription.current.unsubscribes.forEach((cb) => cb());
    }

    if (
      me != null &&
      subscription.current != null &&
      subscription.current.orgId !== me.organizationId
    ) {
      subscription.current.unsubscribes.forEach((cb) => cb());
    }

    if (me != null) {
      const ordersCb = onChildAdded(
        ref(database, `orgs/${me.organizationId}/orders`),
        (snapshot) => {
          const data = snapshot.val();

          if (shouldIngore(data.updated)) {
            return;
          }
          if (data.status === 'PENDING') {
            playBeep();
            enqueueSnackbar(`New order entered`, {
              variant: 'info',
            });
          }
          queryCache.invalidateQueries('orders');
        },
      );
      const ordersUpdateCb = onChildChanged(
        ref(database, `orgs/${me.organizationId}/orders`),
        (snapshot) => {
          const data = snapshot.val();

          if (shouldIngore(data.updated)) {
            return;
          }
          if (data.status === 'CANCELLED') {
            enqueueSnackbar(`An order was cancelled`, {
              variant: 'warning',
            });
          } else if (data.status === 'COMPLETE') {
            enqueueSnackbar(`Order was moved to complete`, {
              variant: 'success',
            });
          } else if (data.status === 'READY') {
            enqueueSnackbar(`Order was moved to ready`, {
              variant: 'info',
            });
          }
          queryCache.invalidateQueries('orders');
        },
      );

      // update menu
      const doUpdateMenu = (snapshot: DataSnapshot) => {
        const data = snapshot.val();
        if (shouldIngore(data.lastUpdated)) {
          return;
        }
        updateMenuStatus(data.virtualId, 'ACTIVE');
        enqueueSnackbar(`Menu updated`, {
          variant: 'success',
        });
        queryCache.invalidateQueries(['menu']);
      };
      const menusAddCb = onChildAdded(
        ref(database, `orgs/${me.organizationId}/menus`),
        doUpdateMenu,
      );
      const menusUpdateCb = onChildChanged(
        ref(database, `orgs/${me.organizationId}/menus`),
        doUpdateMenu,
      );

      // update hours
      const doUpdateHours = (snapshot: DataSnapshot) => {
        const data = snapshot.val();

        if (shouldIngore(data.lastUpdated)) {
          return;
        }
        enqueueSnackbar(`Hours updated`, {
          variant: 'success',
        });
        queryCache.invalidateQueries(['working-hours']);
      };
      const hoursAddedCb = onChildAdded(
        ref(database, `orgs/${me.organizationId}/hours`),
        doUpdateHours,
      );
      const hoursUpdateCb = onChildChanged(
        ref(database, `orgs/${me.organizationId}/hours`),
        doUpdateHours,
      );

      subscription.current = {
        orgId: me.organizationId,
        unsubscribes: [
          ordersCb,
          ordersUpdateCb,
          menusAddCb,
          menusUpdateCb,
          hoursAddedCb,
          hoursUpdateCb,
        ],
      };
    }

    return () => {
      if (subscription.current != null) {
        subscription.current.unsubscribes.forEach((cb) => cb());
      }
    };
  }, [database, enqueueSnackbar, me, queryCache]);

  return socket;
};

export default useSocket;
